/**
 * Firestore REST transport for complete-set, one-shot collection reads.
 *
 * Why REST instead of a live SDK listener: an org-wide live query (e.g. every
 * `usersV1` doc of an 18k-employee org, ~44 MB) becomes one giant target on the
 * single multiplexed WebChannel that ALL of the app's listeners share. It never
 * closes, re-streams every changed doc on each HRIS sync, and when the backend
 * closes that stream (RESOURCE_EXHAUSTED) the SDK backs off to its max delay and
 * re-adds every target, starving the rest of the page. REST runQuery is plain
 * HTTP: paged, parallel, retryable per page, and gone once it completes.
 *
 * Reads go through security rules exactly like the SDK (the user's ID token is
 * the bearer), so no rules change is needed.
 */
import firebase from 'firebase/app';
// Registers firebase.firestore.{Timestamp,GeoPoint,Blob}; already loaded by the
// app's init, imported here so the module is self-contained.
import 'firebase/firestore';

export type RestAuth = { token: string; projectId: string };
export type RestDoc = { id: string; data: Record<string, any> };
export type FetchLike = (
  url: string,
  init: { method: string; headers: Record<string, string>; body: string }
) => Promise<{ ok: boolean; status: number; json: () => Promise<any>; text: () => Promise<string> }>;

export type RestDeps = {
  getAuth: () => Promise<RestAuth>;
  fetch: FetchLike;
  /** Sleep used between retries; injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
};

/** Signed-in user's ID token + the app's project id. */
export const defaultGetAuth = async (): Promise<RestAuth> => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('NOT_AUTHENTICATED');
  const token = await user.getIdToken();
  const projectId = (firebase.app().options as { projectId?: string }).projectId;
  if (!projectId) throw new Error('NO_PROJECT_ID');
  return { token, projectId };
};

export const defaultRestDeps = (): RestDeps => ({
  getAuth: defaultGetAuth,
  fetch: (url, init) => fetch(url, init)
});

const docsRoot = (projectId: string) =>
  `projects/${projectId}/databases/(default)/documents`;

/**
 * Decode a Firestore REST `Value` into the same JS shape the web SDK hands out
 * (Timestamp / GeoPoint / DocumentReference instances, numbers, nested maps and
 * arrays), so consumers that call `.toMillis()` or `instanceof Timestamp` keep
 * working unchanged.
 */
export const fromRestValue = (v: any): any => {
  if (v == null) return undefined;
  if ('nullValue' in v) return null;
  if ('booleanValue' in v) return v.booleanValue;
  if ('stringValue' in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue' in v) return Number(v.doubleValue);
  if ('timestampValue' in v) return toTimestamp(v.timestampValue);
  if ('geoPointValue' in v) {
    const g = v.geoPointValue || {};
    return new firebase.firestore.GeoPoint(g.latitude || 0, g.longitude || 0);
  }
  if ('referenceValue' in v) {
    // ".../documents/<path>" → the in-app ref for that path.
    const path = String(v.referenceValue).split('/documents/')[1] || '';
    return firebase.firestore().doc(path);
  }
  if ('bytesValue' in v) {
    return firebase.firestore.Blob.fromBase64String(v.bytesValue);
  }
  if ('arrayValue' in v) {
    return (v.arrayValue.values || []).map(fromRestValue);
  }
  if ('mapValue' in v) return fromRestFields(v.mapValue.fields);
  return undefined;
};

export const fromRestFields = (fields: any): Record<string, any> => {
  const out: Record<string, any> = {};
  Object.entries(fields || {}).forEach(([k, val]) => {
    out[k] = fromRestValue(val);
  });
  return out;
};

/**
 * RFC3339 → Timestamp, preserving sub-millisecond precision (Date would drop
 * nanos, and some code compares timestamps with isEqual).
 */
export const toTimestamp = (iso: string): firebase.firestore.Timestamp => {
  const m = /^(.*?)(?:\.(\d+))?(Z|[+-]\d\d:\d\d)$/.exec(iso);
  if (!m) return firebase.firestore.Timestamp.fromDate(new Date(iso));
  const seconds = Math.floor(Date.parse(`${m[1]}${m[3]}`) / 1000);
  const nanos = m[2] ? Number(m[2].padEnd(9, '0').slice(0, 9)) : 0;
  return new firebase.firestore.Timestamp(seconds, nanos);
};

const toRestDoc = (doc: any): RestDoc => ({
  id: String(doc.name).split('/').pop() as string,
  data: fromRestFields(doc.fields)
});

/**
 * Fixed doc-id split points. Ranges are [prev, next) in Firestore's id order
 * (UTF-8 byte order for string ids), first unbounded below and last unbounded
 * above, so together they cover EVERY id exactly once whatever the id alphabet.
 * Ids here mix legacy shortids ([A-Za-z0-9_-]) and 12-char hex importer ids, so
 * the split is uneven — that only affects speed (never worse than sequential),
 * never completeness. Must stay sorted ascending.
 */
export const ID_RANGE_BOUNDARIES = ['4', '8', 'G', 'O', 'W', '_', 'c', 'h', 'o'];

export type IdRange = { from?: string; to?: string };

export const idRanges = (boundaries: string[] = ID_RANGE_BOUNDARIES): IdRange[] => {
  const ranges: IdRange[] = [];
  let from: string | undefined;
  boundaries.forEach((b) => {
    ranges.push({ from, to: b });
    from = b;
  });
  ranges.push({ from, to: undefined });
  return ranges;
};

const RETRYABLE = new Set([429, 500, 502, 503, 504]);
const MAX_ATTEMPTS = 4;

const postJson = async (deps: RestDeps, url: string, body: unknown, token: string) => {
  const sleep = deps.sleep || ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const resp = await deps.fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (resp.ok) return resp.json();
      const text = await resp.text();
      const err = Object.assign(new Error(`Firestore REST ${resp.status}: ${text.slice(0, 300)}`), {
        status: resp.status
      });
      if (!RETRYABLE.has(resp.status)) throw err;
      lastErr = err;
    } catch (e: any) {
      // Non-retryable HTTP status → rethrow immediately; network errors retry.
      if (e && typeof e.status === 'number' && !RETRYABLE.has(e.status)) throw e;
      lastErr = e;
    }
    if (attempt < MAX_ATTEMPTS) await sleep(500 * 2 ** (attempt - 1));
  }
  throw lastErr;
};

export type EqualityQuery = {
  collectionId: string;
  field: string;
  value: string;
  pageSize?: number;
};

/** All docs of one id range matching `field == value`, paged by doc id. */
export const runQueryRange = async (
  deps: RestDeps,
  auth: RestAuth,
  q: EqualityQuery,
  range: IdRange
): Promise<RestDoc[]> => {
  const root = docsRoot(auth.projectId);
  const pageSize = q.pageSize || 1000;
  const ref = (id: string) => ({ referenceValue: `${root}/${q.collectionId}/${id}` });
  const filters: any[] = [
    { fieldFilter: { field: { fieldPath: q.field }, op: 'EQUAL', value: { stringValue: q.value } } }
  ];
  if (range.from !== undefined) {
    filters.push({
      fieldFilter: { field: { fieldPath: '__name__' }, op: 'GREATER_THAN_OR_EQUAL', value: ref(range.from) }
    });
  }
  if (range.to !== undefined) {
    filters.push({
      fieldFilter: { field: { fieldPath: '__name__' }, op: 'LESS_THAN', value: ref(range.to) }
    });
  }
  const url = `https://firestore.googleapis.com/v1/${root}:runQuery`;
  const out: RestDoc[] = [];
  let lastName: string | undefined;
  for (;;) {
    const structuredQuery: any = {
      from: [{ collectionId: q.collectionId }],
      where: filters.length === 1 ? filters[0] : { compositeFilter: { op: 'AND', filters } },
      orderBy: [{ field: { fieldPath: '__name__' }, direction: 'ASCENDING' }],
      limit: pageSize
    };
    if (lastName) {
      structuredQuery.startAt = { values: [{ referenceValue: lastName }], before: false };
    }
    const rows = await postJson(deps, url, { structuredQuery }, auth.token);
    const docs = (Array.isArray(rows) ? rows : []).filter((r: any) => r && r.document);
    docs.forEach((r: any) => out.push(toRestDoc(r.document)));
    if (docs.length < pageSize) return out;
    lastName = docs[docs.length - 1].document.name;
  }
};

/**
 * EVERY doc matching `field == value` — fetched as parallel id ranges, each
 * paged. Resolves only once all pages have landed (never a partial set), so
 * search / filter / export / rule matching over the result stay complete.
 */
export const fetchAllByEquality = async (
  deps: RestDeps,
  q: EqualityQuery,
  ranges: IdRange[] = idRanges()
): Promise<RestDoc[]> => {
  const auth = await deps.getAuth();
  const parts = await Promise.all(ranges.map((r) => runQueryRange(deps, auth, q, r)));
  return parts.flat();
};

const BATCH_GET_CHUNK = 300;

/** Docs by id via `documents:batchGet` (chunked). Missing ids map to null. */
export const batchGetByIds = async (
  deps: RestDeps,
  collectionId: string,
  ids: string[]
): Promise<Record<string, RestDoc | null>> => {
  const unique = Array.from(new Set(ids.filter(Boolean)));
  if (!unique.length) return {};
  const auth = await deps.getAuth();
  const root = docsRoot(auth.projectId);
  const url = `https://firestore.googleapis.com/v1/${root}:batchGet`;
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += BATCH_GET_CHUNK) {
    chunks.push(unique.slice(i, i + BATCH_GET_CHUNK));
  }
  const out: Record<string, RestDoc | null> = {};
  await Promise.all(
    chunks.map(async (chunk) => {
      const rows = await postJson(
        deps,
        url,
        { documents: chunk.map((id) => `${root}/${collectionId}/${id}`) },
        auth.token
      );
      (Array.isArray(rows) ? rows : []).forEach((r: any) => {
        if (r && r.found) {
          const d = toRestDoc(r.found);
          out[d.id] = d;
        } else if (r && r.missing) {
          out[String(r.missing).split('/').pop() as string] = null;
        }
      });
    })
  );
  unique.forEach((id) => {
    if (!(id in out)) out[id] = null;
  });
  return out;
};
